'use client';

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { register, clearAuthError } from '../../redux/actions/authActions';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

const step1Schema = Yup.object().shape({
  firstName: Yup.string().required('Prénom requis'),
  lastName: Yup.string().required('Nom requis'),
  email: Yup.string().email('Adresse email invalide').required('Email requis'),
});

const step2Schema = Yup.object().shape({
  companyName: Yup.string().required("Nom de l'entreprise requis"),
  companySiret: Yup.string().required('SIRET requis'),
  companyPhone: Yup.string().required('Téléphone requis'),
  companyAddress: Yup.string().required('Adresse requise'),
});

const step3Schema = Yup.object().shape({
  password: Yup.string()
    .required('Mot de passe requis')
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial'
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre')
    .required('Confirmation du mot de passe requise'),
});

const fullSchema = step1Schema.concat(step2Schema).concat(step3Schema);

const STEP1_FIELDS = ['firstName', 'lastName', 'email'];
const STEP2_FIELDS = ['companyName', 'companySiret', 'companyPhone', 'companyAddress'];

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated } = useSelector(state => state.auth);

  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const invitationToken = queryParams.get('token');

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
    dispatch(clearAuthError());
  }, [isAuthenticated, navigate, dispatch]);

  const handleNext = async (validateForm, setTouched) => {
    if (step === 1) {
      const touched = STEP1_FIELDS.reduce((acc, f) => ({ ...acc, [f]: true }), {});
      setTouched(touched, false);
      const errors = await validateForm();
      const hasErrors = STEP1_FIELDS.some(f => errors[f]);
      if (!hasErrors) setStep(2);
    } else if (step === 2) {
      const touched = STEP2_FIELDS.reduce((acc, f) => ({ ...acc, [f]: true }), {});
      setTouched(touched, false);
      const errors = await validateForm();
      const hasErrors = STEP2_FIELDS.some(f => errors[f]);
      if (!hasErrors) setStep(3);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const userData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        companyName: values.companyName,
        companySiret: values.companySiret,
        companyAddress: values.companyAddress,
        companyPhone: values.companyPhone,
        emergencyContact: values.emergencyContact || undefined,
      };
      if (invitationToken) userData.invitationToken = invitationToken;

      const response = await dispatch(register(userData));
      if (response.requiresVerification) {
        navigate('/login?registered=true');
      } else {
        navigate('/dashboard');
      }
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="register-container">
      <h2 className="auth-title">Créer un compte</h2>
      <p className="auth-subtitle">
        {invitationToken
          ? 'Vous avez été invité à rejoindre Alphafusion File Manager. Complétez votre inscription.'
          : 'Inscrivez-vous pour commencer à utiliser Alphafusion File Manager'}
      </p>

      {/* Stepper */}
      <div className="register-steps">
        <div className={`register-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}>
          <div className="register-step__dot">
            {step > 1 ? <i className="material-icons">check</i> : '1'}
          </div>
          <span className="register-step__label">Informations</span>
        </div>
        <div className="register-step__line" />
        <div className={`register-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'done' : ''}`}>
          <div className="register-step__dot">
            {step > 2 ? <i className="material-icons">check</i> : '2'}
          </div>
          <span className="register-step__label">Entreprise</span>
        </div>
        <div className="register-step__line" />
        <div className={`register-step ${step >= 3 ? 'active' : ''}`}>
          <div className="register-step__dot">3</div>
          <span className="register-step__label">Sécurité</span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          emergencyContact: '',
          companyName: '',
          companySiret: '',
          companyAddress: '',
          companyPhone: '',
          password: '',
          confirmPassword: '',
        }}
        validationSchema={step === 1 ? step1Schema : step === 2 ? step1Schema.concat(step2Schema) : fullSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, validateForm, setTouched }) => (
          <Form className="auth-form">

            {/* ── Step 1 : Informations ── */}
            {step === 1 && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">Prénom *</label>
                    <Field
                      type="text"
                      name="firstName"
                      id="firstName"
                      className={`form-control ${errors.firstName && touched.firstName ? 'is-invalid' : ''}`}
                      placeholder="Prénom"
                    />
                    <ErrorMessage name="firstName" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Nom *</label>
                    <Field
                      type="text"
                      name="lastName"
                      id="lastName"
                      className={`form-control ${errors.lastName && touched.lastName ? 'is-invalid' : ''}`}
                      placeholder="Nom"
                    />
                    <ErrorMessage name="lastName" component="div" className="invalid-feedback" />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <Field
                    type="email"
                    name="email"
                    id="email"
                    className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                    placeholder="votre@email.com"
                  />
                  <ErrorMessage name="email" component="div" className="invalid-feedback" />
                </div>

                <div className="form-group">
                  <label htmlFor="emergencyContact">
                    Contact d'urgence <span className="text-muted">(optionnel)</span>
                  </label>
                  <Field
                    type="text"
                    name="emergencyContact"
                    id="emergencyContact"
                    className="form-control"
                    placeholder="+33 6 00 00 00 00"
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  onClick={() => handleNext(validateForm, setTouched)}
                >
                  Suivant
                  <i className="material-icons" style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }}>arrow_forward</i>
                </button>
              </>
            )}

            {/* ── Step 2 : Entreprise ── */}
            {step === 2 && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companyName">Nom de l'entreprise *</label>
                    <Field
                      type="text"
                      name="companyName"
                      id="companyName"
                      className={`form-control ${errors.companyName && touched.companyName ? 'is-invalid' : ''}`}
                      placeholder="Nom de l'entreprise"
                    />
                    <ErrorMessage name="companyName" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="companySiret">SIRET *</label>
                    <Field
                      type="text"
                      name="companySiret"
                      id="companySiret"
                      className={`form-control ${errors.companySiret && touched.companySiret ? 'is-invalid' : ''}`}
                      placeholder="12345678900012"
                    />
                    <ErrorMessage name="companySiret" component="div" className="invalid-feedback" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companyPhone">Téléphone *</label>
                    <Field
                      type="text"
                      name="companyPhone"
                      id="companyPhone"
                      className={`form-control ${errors.companyPhone && touched.companyPhone ? 'is-invalid' : ''}`}
                      placeholder="+33 1 00 00 00 00"
                    />
                    <ErrorMessage name="companyPhone" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="companyAddress">Adresse *</label>
                    <Field
                      type="text"
                      name="companyAddress"
                      id="companyAddress"
                      className={`form-control ${errors.companyAddress && touched.companyAddress ? 'is-invalid' : ''}`}
                      placeholder="Adresse complète"
                    />
                    <ErrorMessage name="companyAddress" component="div" className="invalid-feedback" />
                  </div>
                </div>

                <div className="register-step2-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setStep(1)}
                  >
                    <i className="material-icons" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>arrow_back</i>
                    Retour
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleNext(validateForm, setTouched)}
                  >
                    Suivant
                    <i className="material-icons" style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }}>arrow_forward</i>
                  </button>
                </div>
              </>
            )}

            {/* ── Step 3 : Sécurité ── */}
            {step === 3 && (
              <>
                <div className="form-group">
                  <label htmlFor="password">Mot de passe *</label>
                  <div className="password-field">
                    <Field
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      id="password"
                      className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                      placeholder="Mot de passe"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(v => !v)}
                    >
                      <i className="material-icons">{showPassword ? 'visibility_off' : 'visibility'}</i>
                    </button>
                  </div>
                  <ErrorMessage name="password" component="div" className="invalid-feedback" />
                  <small className="form-text text-muted">
                    Au moins 8 caractères, avec une majuscule, une minuscule, un chiffre et un caractère spécial.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmer le mot de passe *</label>
                  <div className="password-field">
                    <Field
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      id="confirmPassword"
                      className={`form-control ${errors.confirmPassword && touched.confirmPassword ? 'is-invalid' : ''}`}
                      placeholder="Confirmez votre mot de passe"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(v => !v)}
                    >
                      <i className="material-icons">{showConfirmPassword ? 'visibility_off' : 'visibility'}</i>
                    </button>
                  </div>
                  <ErrorMessage name="confirmPassword" component="div" className="invalid-feedback" />
                </div>

                <div className="register-step2-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setStep(2)}
                  >
                    <i className="material-icons" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>arrow_back</i>
                    Retour
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || loading}
                  >
                    {loading ? 'Inscription...' : 'S\'inscrire'}
                  </button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>

      <div className="auth-links">
        <p>
          Vous avez déjà un compte ?{' '}
          <Link to="/login" className="auth-link">Se connecter</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
